import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { SmallOptionIcon } from "components/icons/SmallOptionIcon";
import {
  CLICKED_ALREADY_UPLOADED_ON_RECORDS_AND_SETUP,
  CLICKED_MARK_AS_UNAVAILABLE_ON_RECORDS_AND_SETUP,
  CLICKED_UPLOAD_DOCUMENT_ON_RECORDS_AND_SETUP,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useToast } from "hooks/useToast";
import { ReactElement } from "react";
import { useDropzone } from "react-dropzone";
import { BackendError } from "types/utils/error";
import {
  DOCUMENT_TYPE_STATUS,
  DocumentTypeStatus,
  RequiredItem,
  useBulkUploadDocumentsMutation,
  useSetDocumentTypeStatusMutation,
} from "v2/store/entityDocumentsApi";

type RequiredRowActionsProps = {
  entityId: string;
  item: RequiredItem;
};

export const RequiredRowActions = ({
  entityId,
  item,
}: RequiredRowActionsProps): ReactElement => {
  const { successToast, alertToast } = useToast();
  const { trackEvent } = useAnalytics();

  const [bulkUpload, { isLoading: isUploading }] =
    useBulkUploadDocumentsMutation();
  const [setStatus, { isLoading: isSettingStatus }] =
    useSetDocumentTypeStatusMutation();

  const uploadFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;

    try {
      await bulkUpload({ entityId, files, documentTypeId: item.id }).unwrap();
      successToast({ message: "Document uploaded" });
    } catch (e) {
      alertToast({ message: (e as BackendError)?.data?.error?.message });
    }
  };

  const handleDrop = (files: File[]): void => void uploadFiles(files);

  const { getInputProps, open } = useDropzone({
    onDrop: handleDrop,
    noDrag: true,
  });

  const handleSetStatus = async (status: DocumentTypeStatus): Promise<void> => {
    try {
      await setStatus({ entityId, documentTypeId: item.id, status }).unwrap();
    } catch (e) {
      alertToast({ message: (e as BackendError)?.data?.error?.message });
    }
  };

  const documentPayload = {
    document_name: item.label,
    section: item.section,
  };

  const handleUploadDocument = (): void => {
    trackEvent(CLICKED_UPLOAD_DOCUMENT_ON_RECORDS_AND_SETUP, documentPayload);
    open();
  };

  const handleMarkUnavailable = (): void => {
    trackEvent(
      CLICKED_MARK_AS_UNAVAILABLE_ON_RECORDS_AND_SETUP,
      documentPayload
    );
    void handleSetStatus(DOCUMENT_TYPE_STATUS.UNAVAILABLE);
  };

  const handleMarkAlreadyUploaded = (): void => {
    trackEvent(CLICKED_ALREADY_UPLOADED_ON_RECORDS_AND_SETUP, documentPayload);
    void handleSetStatus(DOCUMENT_TYPE_STATUS.ALREADY_UPLOADED);
  };

  const isBusy = isUploading || isSettingStatus;

  return (
    <>
      <input {...getInputProps()} />

      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button
            customType="ghost_icon"
            size="xsmall"
            aria-label="Document actions"
            disabled={isBusy}
            className="t-text-neutral-40"
          >
            <SmallOptionIcon
              color="currentColor"
              backgroundColor="currentColor"
            />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" sideOffset={4}>
            <Dropdown.Item onSelect={handleUploadDocument}>
              Upload document
            </Dropdown.Item>
            <Dropdown.Item onSelect={handleMarkUnavailable}>
              Mark as unavailable
            </Dropdown.Item>
            <Dropdown.Item onSelect={handleMarkAlreadyUploaded}>
              Already uploaded
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </>
  );
};
